/**
 * JWT authentication module — JWT authentication (Phase 1a).
 *
 * Tokens are stored in memory only (never localStorage/sessionStorage) so they
 * cannot be exfiltrated by XSS. The access token is refreshed silently ~1 minute
 * before expiry using the refresh token.
 *
 * Public surface:
 *   login(email, password)  → user object
 *   logout()
 *   getAccessToken()        → string | null
 *   getUser()               → user object | null
 *   onAuthStateChange(fn)   → unsubscribe
 *   refreshIfNeeded()       → Promise<string | null>
 */

import {clearAwpSession, createNewSession, getOrCreateSession} from '../session/awpSessionContext';
import {clearStoredServiceBootId} from '../session/serviceEpochStorage';
import {captureServiceEpochFromHealth} from '../session/serviceEpochGuard';

const AWP_BASE = process.env.REACT_APP_AWP_API_BASE || '';

const STORAGE_KEY = 'dash_refresh_token';

// ── In-memory token store ─────────────────────────────────────────────────────

let _accessToken = null;
let _refreshToken = null;
let _user = null;
let _refreshTimer = null;
const _listeners = new Set();

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseJwtPayload(token) {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
}

function scheduleRefresh(accessToken) {
    if (_refreshTimer) clearTimeout(_refreshTimer);
    const payload = parseJwtPayload(accessToken);
    if (!payload?.exp) return;
    const msUntilExpiry = payload.exp * 1000 - Date.now();
    const msUntilRefresh = Math.max(msUntilExpiry - 60_000, 10_000); // 1 min before expiry
    _refreshTimer = setTimeout(() => silentRefresh(), msUntilRefresh);
}

function notifyListeners(user) {
    for (const fn of _listeners) {
        try { fn(user); } catch {}
    }
}

function setSession(accessToken, refreshToken, user) {
    _accessToken = accessToken;
    if (refreshToken) {
        _refreshToken = refreshToken;
        try { localStorage.setItem(STORAGE_KEY, refreshToken); } catch {}
    }
    _user = user;
    // Mirror access token to localStorage so all modules reading
    // localStorage.getItem('access_token') get a valid token.
    // The access token is short-lived (24h), so localStorage is acceptable here.
    try {
        if (accessToken) localStorage.setItem('access_token', accessToken);
        else localStorage.removeItem('access_token');
    } catch {}
    if (accessToken) scheduleRefresh(accessToken);
    notifyListeners(user);
}

function clearSession() {
    _accessToken = null;
    _refreshToken = null;
    _user = null;
    if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null; }
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    try { localStorage.removeItem('access_token'); } catch {}
    clearStoredServiceBootId();
    clearAwpSession();
    notifyListeners(null);
}

async function fetchMe(accessToken) {
    const res = await fetch(`${AWP_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    // Return a user object shape compatible with existing JWT-aware code
    const roles = Array.isArray(data.roles) && data.roles.length > 0
        ? data.roles
        : [data.role].filter(Boolean);
    return {
        uid: data.userId,
        userId: data.userId,
        email: data.email,
        displayName: data.displayName || data.firstName || data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,   // primary role (backward-compat)
        roles,             // all roles (multi-role)
        isAdmin: data.isAdmin,
        hasAvatar: Boolean(data.hasAvatar),
        profilePicture: data.profilePicture || null,
    };
}

async function silentRefresh() {
    if (!_refreshToken) return null;
    try {
        const res = await fetch(`${AWP_BASE}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: _refreshToken }),
        });
        if (!res.ok) { clearSession(); return null; }
        const { accessToken } = await res.json();
        const user = await fetchMe(accessToken);
        setSession(accessToken, null, user);
        return accessToken;
    } catch {
        clearSession();
        return null;
    }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Sign in with email + password. Returns the user object on success.
 * Throws with a descriptive message on failure.
 */
export async function login({ email, password }) {
    const res = await fetch(`${AWP_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Login failed (${res.status})`);
    }
    const { accessToken, refreshToken } = await res.json();
    const user = await fetchMe(accessToken);
    if (!user) throw new Error('Failed to load user profile after login');
    setSession(accessToken, refreshToken, user);
    createNewSession(user.uid);
    captureServiceEpochFromHealth().catch(() => {});
    return user;
}

/**
 * Sign out — clears in-memory tokens and notifies listeners.
 */
export async function logout() {
    if (_accessToken) {
        try {
            await fetch(`${AWP_BASE}/api/auth/logout`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${_accessToken}` },
            });
        } catch {}
    }
    clearSession();
}

/** Returns the current access token (may be null if not authenticated). */
export function getAccessToken() {
    return _accessToken;
}

/** Returns the current user object (may be null if not authenticated). */
export function getUser() {
    return _user;
}

/**
 * Returns true if a persisted session exists in storage that has not yet been
 * restored into memory. Used by UserProvider to stay in the loading state on a
 * fresh page load so the login page doesn't flash before the session restores.
 */
export function hasPendingStoredSession() {
    if (_accessToken && _user) return false;
    try {
        return Boolean(localStorage.getItem(STORAGE_KEY));
    } catch {
        return false;
    }
}

/**
 * Subscribe to auth state changes. The callback receives the user object or null.
 * Returns an unsubscribe function.
 */
export function onAuthStateChange(fn) {
    _listeners.add(fn);
    // Fire immediately with the current state
    try { fn(_user); } catch {}
    return () => _listeners.delete(fn);
}

/**
 * Restores a previous session from localStorage on app startup.
 * Call this once on mount; it silently exchanges the persisted refresh token
 * for a fresh access token. Returns the user object or null.
 */
export async function initFromStorage() {
    // Already have a live session (e.g. hot-reload)
    if (_accessToken && _user) return _user;

    let storedRefresh = null;
    try { storedRefresh = localStorage.getItem(STORAGE_KEY); } catch {}
    if (!storedRefresh) return null;

    _refreshToken = storedRefresh;
    return await silentRefresh().then(async () => {
        if (_user?.uid) {
            getOrCreateSession(_user.uid);
        }
        await captureServiceEpochFromHealth();
        return _user;
    });
}

/**
 * Refreshes the access token if it has expired (or will expire in < 1 min).
 * Returns the (possibly new) access token, or null if the session is gone.
 */
export async function refreshIfNeeded() {
    if (!_accessToken) return null;
    const payload = parseJwtPayload(_accessToken);
    if (!payload?.exp) return _accessToken;
    const msUntilExpiry = payload.exp * 1000 - Date.now();
    if (msUntilExpiry < 60_000) {
        return await silentRefresh();
    }
    return _accessToken;
}

/**
 * Update the current user's profile via PATCH /api/users/{userId}/profile.
 * Compatible drop-in for legacy updateUserAuth().
 */
export async function updateUserAuth({ displayName, email, password, currentPassword } = {}) {
    if (!_accessToken || !_user) throw new Error('No authenticated user to update');
    const updates = {};

    if (email || password) {
        if (!currentPassword) throw new Error('currentPassword is required to update email or password');
    }

    // PATCH profile
    if (displayName !== undefined || email !== undefined) {
        const body = {};
        if (displayName !== undefined) body.displayName = displayName;
        if (email !== undefined) body.email = email;
        if (currentPassword !== undefined) body.currentPassword = currentPassword;

        const res = await fetch(`${AWP_BASE}/api/users/${_user.uid}/profile`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${_accessToken}`,
            },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Profile update failed');
        }
        if (displayName !== undefined) updates.profile = true;
        if (email !== undefined) updates.email = true;
    }

    if (password && currentPassword) {
        const res = await fetch(`${AWP_BASE}/api/users/${_user.uid}/change-password`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${_accessToken}`,
            },
            body: JSON.stringify({ currentPassword, newPassword: password }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Password change failed');
        }
        updates.password = true;
    }

    // Refresh the user object from server
    const freshUser = await fetchMe(_accessToken);
    if (freshUser) {
        _user = freshUser;
        notifyListeners(_user);
    }
    return { user: _user, updates };
}
